import { useEffect, useState } from 'react'

/**
 * The breakpoints, in one place and matching `styles/layout.css` exactly. Two definitions of
 * "phone" that drift apart is a bug you can only see on a device, so there is only one.
 */
export const PHONE_MAX = 639
export const PANE_MIN = 1024

export type Layout = 'phone' | 'tablet' | 'desk'

function read(): Layout {
  if (typeof window === 'undefined') return 'desk'
  if (window.matchMedia(`(min-width: ${PANE_MIN}px)`).matches) return 'desk'
  if (window.matchMedia(`(max-width: ${PHONE_MAX}px)`).matches) return 'phone'
  return 'tablet'
}

/** Current layout band, updated on resize and rotation. */
export function useLayout(): Layout {
  const [layout, setLayout] = useState<Layout>(read)
  useEffect(() => {
    const phone = window.matchMedia(`(max-width: ${PHONE_MAX}px)`)
    const desk = window.matchMedia(`(min-width: ${PANE_MIN}px)`)
    const update = () => setLayout(read())
    phone.addEventListener('change', update)
    desk.addEventListener('change', update)
    return () => {
      phone.removeEventListener('change', update)
      desk.removeEventListener('change', update)
    }
  }, [])
  return layout
}

/**
 * ★★ OPEN/CLOSED FOR AN OVERLAY-OR-PANE PANEL.
 *
 * The rule (David, 2026-07-23): the menu slides out, you pick something, and on mobile it slides
 * back by itself. The two halves people get wrong:
 *
 *   1. AUTO-CLOSE IS MOBILE ONLY. At desktop the menu is a persistent pane and closing it every
 *      time you picked a screen would be maddening.
 *   2. THE DEFAULT FOLLOWS THE BREAKPOINT, BUT AN EXPLICIT TOGGLE WINS WITHIN IT. Otherwise
 *      rotating a tablet from portrait to landscape strands you with a menu you closed for a
 *      narrower layout — or worse, re-opens one you deliberately shut on every resize tick.
 *      A toggle is remembered until the BAND changes, at which point the new band's default wins.
 */
export function usePanel(layout: Layout): {
  open: boolean
  overlay: boolean
  toggle: () => void
  close: () => void
  /** Call after a selection: closes only where closing is the right behaviour. */
  closeIfOverlay: () => void
} {
  const overlay = layout !== 'desk'
  const [choice, setChoice] = useState<{ band: Layout; open: boolean } | null>(null)

  const open = choice && choice.band === layout ? choice.open : !overlay

  return {
    open,
    overlay,
    toggle: () => setChoice({ band: layout, open: !open }),
    close: () => setChoice({ band: layout, open: false }),
    closeIfOverlay: () => {
      if (overlay) setChoice({ band: layout, open: false })
    },
  }
}
